import { useCallback, useEffect, useRef, useState } from 'react';
import useAppContext from './useAppContext';
import useClockDataListener from '../views/Klydos/hooks/useClockDataListener';
import useUpdateClockApplicationMutation from '../views/Klydos/hooks/useUpdateClockApplicationMutation';
import useAddClockToUserMutation from '../views/Klydos/hooks/useAddClockToUserMutation';
import { AlertType } from '../design-system/DSAlert';
import { PreviewUnavailableReason } from '../views/Klydos/components/PreviewUnavailableModal';
import { Klydo } from '../api/klydo';
import { PENDING_KLYDO_MIN_VERSION } from '../firebase/firebaseService';
import { getRequiredClockVersion } from '../utils/clockContentSupport';

export enum SendableContentKind {
  Klydo = 'klydo',
  Collection = 'collection',
}

type UseSendToClockProps = {
  /** What is being sent — drives the modal copy and the queue-version floor. */
  kind: SendableContentKind;
  /** False while there is nothing to send — no button and no clock listener. */
  hasContent: boolean;
  /** Content heading for the clock, used for the firmware version gate. */
  klydos: Pick<Klydo, 'subtitle' | 'capabilitiesV2'>[];
  /** A klydo's "already sent" flag, from the per-browser localStorage marker. */
  sentMarker?: boolean;
  /** A collection id to look for in the clock's own machineCollections list. */
  sentCollectionId?: string;
  /** Fires the actual send. Kept out of here so each page owns its mutation. */
  send: () => void;
  isSending: boolean;
};

/**
 * Shared logic behind the "Send to Clock" button on the klydo and collection
 * pages: the clock gate, the update modal, and the success alert.
 *
 * An offline clock is not blocked — the send is queued and the clock picks it up
 * when it next boots. A clock that cannot render the content is blocked, since
 * queueing it would just fail quietly on the device.
 */
const useSendToClock = ({
  kind,
  hasContent,
  klydos,
  sentMarker = false,
  sentCollectionId,
  send,
  isSending,
}: UseSendToClockProps) => {
  const { clock, showAlert, refreshToken } = useAppContext();
  const [unavailableReason, setUnavailableReason] =
    useState<PreviewUnavailableReason | null>(null);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  // Set when the artist reached the connect modal from the send button, so the
  // send resumes on its own once the clock claim arrives.
  const sendAfterConnect = useRef(false);

  const { data: clockData, isLoading: isLoadingClockData } =
    useClockDataListener(hasContent ? clock : null);

  const { mutate: updateClock, isPending: isUpdating } =
    useUpdateClockApplicationMutation({
      onSuccess: () => {
        setUnavailableReason(null);
        showAlert({
          message: 'Clock update has been initiated',
          alertType: AlertType.SUCCESS,
        });
      },
    });

  // Only a klydo goes through pendingKlydos; a collection is picked up straight
  // from machineCollections, so it has no queue-support floor.
  const usesPendingKlydos = kind === SendableContentKind.Klydo;

  const requiredVersion = Math.max(
    getRequiredClockVersion(klydos),
    usesPendingKlydos ? PENDING_KLYDO_MIN_VERSION : 0,
  );

  // A collection stays in the clock's machineCollections, so its state is read
  // live and self-heals when the user deletes it on the device. A klydo has no
  // such record, so it falls back to the localStorage marker.
  const isOnClock =
    sentMarker ||
    (!!sentCollectionId &&
      !!clockData?.collectionIds?.includes(sentCollectionId));

  // A collection that is genuinely on the clock has nothing to re-send, and the
  // live check will clear once it is deleted there. A klydo's marker never
  // clears and the API happily re-queues, so blocking it would trap the artist
  // in the most likely case: "it is gone from my clock, send it again".
  const isAlreadyOnClock = !usesPendingKlydos && isOnClock;

  const attemptSend = useCallback(() => {
    // A clock that never reported a version is treated as too old whenever the
    // content has a floor at all — the API rejects those sends too, so the
    // update modal is more useful than letting the request fail with an alert.
    if (requiredVersion > 0 && (clockData?.version ?? 0) < requiredVersion) {
      setUnavailableReason(PreviewUnavailableReason.NEEDS_UPDATE);
      return;
    }

    send();
  }, [clockData?.version, requiredVersion, send]);

  const { mutate: addClock, isPending: isConnecting } =
    useAddClockToUserMutation({
      suppressErrorAlert: true,
      onSuccess: async () => {
        // The connect already succeeded server-side; a failed token refresh only
        // delays the claim, so it must not reject the success path.
        await refreshToken().catch(() => undefined);
        setIsConnectModalOpen(false);
        showAlert({
          message: 'Your device has been connected',
          alertType: AlertType.SUCCESS,
        });
      },
      onError: () => {
        // The pending send is deliberately kept: a failed connect leaves the
        // clock claim unset, so nothing can be sent anywhere, and the modal
        // stays open. Clearing it here would mean a mistyped serial silently
        // swallows the send the artist asked for. It is cleared when they close
        // the modal instead.
        showAlert({
          message: 'Failed to connect your device',
          alertType: AlertType.ERROR,
        });
      },
    });

  // Resume the send the artist originally asked for. Waits for the clock data
  // too, not just the claim: the version gate reads `clockData.version`, and
  // acting before the machines snapshot lands would wrongly report the clock as
  // too old.
  useEffect(() => {
    if (!sendAfterConnect.current || !clock || isLoadingClockData) return;
    sendAfterConnect.current = false;
    attemptSend();
  }, [clock, isLoadingClockData, attemptSend]);

  const onClick = useCallback(() => {
    if (isAlreadyOnClock) return;

    // No clock yet: collect the serial first, then pick the send back up. Same
    // entry point as the draft klydo page's "Preview On ..." button.
    if (!clock) {
      sendAfterConnect.current = true;
      setIsConnectModalOpen(true);
      return;
    }

    attemptSend();
  }, [clock, isAlreadyOnClock, attemptSend]);

  const onConnectClock = useCallback(
    (serialNumber: string) => {
      addClock({ clockId: serialNumber });
    },
    [addClock],
  );

  const closeConnectModal = useCallback(() => {
    sendAfterConnect.current = false;
    setIsConnectModalOpen(false);
  }, []);

  const onUpdateClock = useCallback(() => {
    if (!clock) return;
    updateClock({ clockId: clock });
  }, [clock, updateClock]);

  return {
    // Shown even with no clock connected: the button then opens the connect
    // modal, mirroring the draft klydo page's "Preview On ..." button.
    canSend: hasContent,
    clockName: clockData?.idf,
    kind,
    /** Already delivered: "Sent" for a collection, "Send again" for a klydo. */
    isOnClock,
    isDisabled: isAlreadyOnClock || isSending || isLoadingClockData,
    isSending,
    isConnectModalOpen,
    closeConnectModal,
    onConnectClock,
    isConnecting,
    unavailableReason,
    closeUnavailable: () => setUnavailableReason(null),
    onClick,
    onUpdateClock,
    isUpdating,
  };
};

export default useSendToClock;
